import React, { useMemo } from 'react';
import {
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { FiChevronUp, FiChevronDown } from 'react-icons/fi';
import classNames from 'classnames';
import { InPageSpinner } from '@components/spinners/in-page-spinner';
import { DataTableProps } from './types';
import styles from './styles.module.scss';

/*
 * Generic tanstack-powered data table styled to V1's dark theme. Supports
 * either externally controlled sorting (server-side) or internal sorting
 * when `sorting`/`onSortingChange` are omitted.
 */
export function DataTable<T>({
    data,
    columns,
    isLoading = false,
    sorting,
    onSortingChange,
    onRowClick,
    emptyText = 'No data available',
    rowKey,
    className,
    minHeight,
    maxHeight,
}: DataTableProps<T>) {
    const isManualSorting = sorting !== undefined && onSortingChange !== undefined;

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: isManualSorting ? undefined : getSortedRowModel(),
        manualSorting: isManualSorting,
        state: isManualSorting ? { sorting } : undefined,
        onSortingChange: isManualSorting
            ? (updater) => {
                const next = typeof updater === 'function' ? updater(sorting ?? []) : updater;
                onSortingChange?.(next);
            }
            : undefined,
    });

    const wrapStyle = useMemo(
        () => ({
            minHeight,
            maxHeight,
            overflowY: maxHeight ? ('auto' as const) : undefined,
        }),
        [minHeight, maxHeight]
    );

    return (
        <div className={classNames(styles.dataTableWrap, className)} style={wrapStyle}>
            <table className={styles.dataTable}>
                <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => {
                                const canSort = header.column.getCanSort();
                                const sortDirection = header.column.getIsSorted();
                                return (
                                    <th
                                        key={header.id}
                                        style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}
                                        className={classNames({ [styles.sortable]: canSort })}
                                        onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                                    >
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                        {canSort && (
                                            <span className={styles.sortIcon}>
                                                {sortDirection === 'asc' ? (
                                                    <FiChevronUp size={12} />
                                                ) : sortDirection === 'desc' ? (
                                                    <FiChevronDown size={12} />
                                                ) : null}
                                            </span>
                                        )}
                                    </th>
                                );
                            })}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {table.getRowModel().rows.map((row, index) => (
                        <tr
                            key={rowKey ? rowKey(row.original, index) : row.id}
                            className={classNames({ [styles.clickable]: !!onRowClick })}
                            onClick={() => onRowClick?.(row.original)}
                        >
                            {row.getVisibleCells().map((cell) => (
                                <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
            {!isLoading && data.length === 0 && <div className={styles.emptyState}>{emptyText}</div>}
            {isLoading && (
                <div className={styles.loadingOverlay}>
                    <InPageSpinner size="Small" />
                </div>
            )}
        </div>
    );
}
